import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { CloseCircle, DocumentText1, Sms } from "iconsax-react";
import { formatLocalDateTime } from "../../utils/dateTime";

const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const BORDER = "#E5E7EB";
const TEXT = "#344054";

function formatLabelValue(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function FieldRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", gap: 1, mt: 0.55 }}>
      <Typography sx={{ fontSize: 11.5, color: BLUE, fontWeight: 700 }}>
        {label}:
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: TEXT }}>{value || "-"}</Typography>
    </Box>
  );
}

export default function MessagePreviewDialog({
  open,
  onClose,
  message,
  onMarkRead,
  onForward,
  onReply,
}) {
  const attachments = Array.isArray(message?.attachments) ? message.attachments : [];
  const formattedDate = message?.sent_at
    ? formatLocalDateTime(message.sent_at, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }, "-")
    : "-";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 360,
          maxWidth: "calc(100vw - 32px)",
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2.2 }}>
          <Box data-popup-header sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Sms size="14" color={PURPLE} />
              <Typography data-popup-title sx={{ fontSize: 12, color: BLUE, fontWeight: 700 }}>
                Email Preview
              </Typography>
            </Box>

            <IconButton data-popup-close onClick={onClose} size="small" sx={{ p: 0 }}>
              <CloseCircle size="18" color={PURPLE} variant="Bold" />
            </IconButton>
          </Box>

          <Typography sx={{ mt: 2.1, color: TEXT, fontSize: 16, fontWeight: 700 }}>
            {message?.subject || "Message preview"}
          </Typography>

          <Box sx={{ mt: 1.5 }}>
            <FieldRow label="From" value={message?.from_name || message?.from_email} />
            <FieldRow label="To" value={message?.to_email} />
            <FieldRow label="Date" value={formattedDate} />
            {message?.sequence_name ? (
              <FieldRow label="Sequence" value={message.sequence_name} />
            ) : null}
            {message?.message_provider ? (
              <FieldRow
                label="Provider"
                value={formatLabelValue(message.message_provider)}
              />
            ) : null}
          </Box>

          {attachments.length ? (
            <>
              <Divider sx={{ my: 1.5, borderColor: BORDER }} />
              <Typography sx={{ fontSize: 11.5, color: BLUE, fontWeight: 700 }}>
                Attachments:
              </Typography>
              <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 0.8 }}>
                <DocumentText1 size="14" color={PURPLE} />
                <Typography sx={{ fontSize: 11.5, color: PURPLE, fontWeight: 700 }}>
                  {attachments[0]?.name}
                </Typography>
              </Box>
            </>
          ) : null}

          <Divider sx={{ my: 1.5, borderColor: BORDER }} />

          <Typography sx={{ fontSize: 11.5, color: BLUE, fontWeight: 700, mb: 0.75 }}>
            Message:
          </Typography>
          <Typography
            sx={{
              color: TEXT,
              fontSize: 12.5,
              lineHeight: 1.9,
              whiteSpace: "pre-line",
            }}
          >
            {message?.body_text || message?.preview_text || "No message body available."}
          </Typography>
        </Box>

        <Box
          sx={{
            px: 2.2,
            pb: 2.1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={onMarkRead}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              borderColor: PURPLE,
              color: PURPLE,
              fontSize: 11.5,
              fontWeight: 700,
            }}
          >
            Mark as Read
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              type="button"
              onClick={onForward}
              sx={{
                minWidth: 74,
                textTransform: "none",
                borderRadius: 999,
                color: "#fff",
                backgroundColor: PURPLE,
                fontSize: 11.5,
                fontWeight: 700,
                "&:hover": { backgroundColor: PURPLE },
              }}
            >
              Forward
            </Button>
            <Button
              type="button"
              onClick={onReply}
              sx={{
                minWidth: 64,
                textTransform: "none",
                borderRadius: 999,
                color: "#fff",
                backgroundColor: PURPLE,
                fontSize: 11.5,
                fontWeight: 700,
                "&:hover": { backgroundColor: PURPLE },
              }}
            >
              Reply
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
